import { useState } from "react";
import api, { fmtTRY } from "../../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Target, Pencil, Trash2, AlertTriangle } from "lucide-react";
import CategorySelect from "./CategorySelect";

const STYLE = {
  normal: { bar: "bg-emerald-600", text: "text-emerald-700", label: "" },
  uyari: { bar: "bg-amber-500", text: "text-amber-700", label: "%80'e ulaştı" },
  asildi: { bar: "bg-rose-600", text: "text-rose-700", label: "Bütçe aşıldı" },
};

const BudgetRow = ({ b, onEdit, onDelete }) => {
  const s = STYLE[b.status] || STYLE.normal;
  return (
    <li className="py-2.5" data-testid={`budget-row-${b.category}`}>
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="font-semibold truncate flex items-center gap-1.5">
          {b.status !== "normal" && <AlertTriangle size={13} className={s.text} />}{b.category}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          <span className={`font-semibold ${s.text}`} data-testid={`budget-pct-${b.category}`}>%{b.percent}</span>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(b)} data-testid={`budget-edit-${b.category}`}><Pencil size={13} /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onDelete(b)} data-testid={`budget-delete-${b.category}`}><Trash2 size={13} className="text-rose-600" /></Button>
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden mt-1">
        <div className={`h-full ${s.bar} transition-[width] duration-500`} style={{ width: `${Math.min(b.percent, 100)}%` }} />
      </div>
      <div className="text-xs text-slate-500 mt-1 flex justify-between">
        <span>{fmtTRY(b.spent)} / {fmtTRY(b.monthly_limit)}</span>
        {s.label && <span className={`font-semibold ${s.text}`} data-testid={`budget-status-${b.category}`}>{s.label}</span>}
      </div>
    </li>
  );
};

export default function ExpenseBudgets({ budgets, categories, onChanged }) {
  const [form, setForm] = useState(null);

  const save = async () => {
    if (!form.category) return toast.error("Kategori seçin");
    if (!(Number(form.monthly_limit) > 0)) return toast.error("Geçerli bir limit girin");
    try { await api.put("/expense-budgets", { category: form.category, monthly_limit: Number(form.monthly_limit) }); toast.success("Bütçe kaydedildi"); setForm(null); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };
  const remove = async (b) => {
    if (!window.confirm(`${b.category} bütçesi kaldırılsın mı?`)) return;
    await api.put("/expense-budgets", { category: b.category, monthly_limit: 0 }); toast.success("Bütçe kaldırıldı"); onChanged();
  };

  return (
    <Card className="border-slate-200" data-testid="expense-budgets">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 font-bold text-slate-900"><Target size={16} className="text-[#1B4D3E]" /> Aylık Bütçe Limitleri</div>
          <Button size="sm" variant="outline" onClick={() => setForm({ category: "", monthly_limit: "" })} data-testid="add-budget-btn">Limit Ekle</Button>
        </div>
        {budgets.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">Henüz bütçe limiti yok. Kategori başına aylık limit belirleyin; %80'de uyarı alırsınız.</div>
        ) : (
          <ul className="divide-y divide-slate-100">{budgets.map((b) => <BudgetRow key={b.category} b={b} onEdit={(x) => setForm({ category: x.category, monthly_limit: x.monthly_limit, locked: true })} onDelete={remove} />)}</ul>
        )}
      </CardContent>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="budget-dialog">
          <DialogHeader><DialogTitle>Aylık Bütçe Limiti</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div><Label>Kategori</Label>{form.locked ? <div className="font-semibold text-sm py-2">{form.category}</div> : <CategorySelect value={form.category} onChange={(v) => setForm({ ...form, category: v })} categories={categories} testid="budget-category" />}</div>
              <div><Label>Aylık Limit (₺)</Label><Input type="number" min="0" value={form.monthly_limit} onChange={(e) => setForm({ ...form, monthly_limit: e.target.value })} data-testid="budget-limit" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>İptal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#163e32]" onClick={save} data-testid="budget-save-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
